import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

export interface Alert {
  id: string
  user_id: number
  alert_type: string
  notification_type: string
  severity: 'info' | 'nudge' | 'warning' | 'critical'
  title: string
  message: string
  action?: {
    label: string
    url: string
    action_type: string
  }
  priority_score: number
  display_mode: 'passive' | 'prominent' | 'interruptive'
  explain: {
    summary: string
    bullets: string[]
  }
  requires_ack: boolean
  created_at: string
  reason_codes?: string[]
  evidence?: Record<string, any>
}

export interface AlertsResponse {
  success: boolean
  alerts: Alert[]
  interruptive_candidate: Alert | null
  count: number
  error?: string
}

export interface CoachAlertsApi {
  alerts: Alert[]
  interruptiveCandidate: Alert | null
  loading: boolean
  fetchAlerts: () => Promise<void>
  markShown: (alertId: string, context: 'login' | 'refresh' | 'route_change') => Promise<void>
  dismiss: (alertId: string) => Promise<void>
  snooze: (alertId: string, minutes: number) => Promise<void>
  acknowledge: (alertId: string) => Promise<void>
}

/**
 * The ONE live alerts feed, published by the app-root provider.
 *
 * Defect fix 2026-08-29: this hook opened its own poller in every component
 * that mounted it (App shell + Today), so /api/orchestrator/alerts was hit
 * once per mount and then once a minute PER INSTANCE, forever. The hook's
 * public shape is unchanged; it now reads the shared feed when one exists.
 */
export const CoachAlertsContext = createContext<CoachAlertsApi | null>(null)

/**
 * The fetching implementation. Mounted exactly once (by the provider) in the
 * app; `enabled` is false for the fallback instance inside `useCoachAlerts`
 * when a provider is present, so no second poller ever starts.
 */
export function useCoachAlertsSource(pollIntervalMs: number = 60000, enabled: boolean = true): CoachAlertsApi {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [interruptiveCandidate, setInterruptiveCandidate] = useState<Alert | null>(null)
  const [loading, setLoading] = useState(false)
  const location = useLocation()

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true)
      const res = await fetch(
        '/api/orchestrator/alerts?limit=3&unseen_only=false&include_interruptive_candidate=true',
        {
          credentials: 'include',
        }
      )
      if (res.ok) {
        const data: AlertsResponse = await res.json()
        if (data.success) {
          setAlerts(data.alerts || [])
          setInterruptiveCandidate(data.interruptive_candidate || null)
        }
      }
    } catch (err) {
      // Silent fail -- alerts are non-critical, graceful degradation
      console.warn('Failed to fetch alerts:', err)
    } finally {
      setLoading(false)
    }
  }, [])

  const markShown = useCallback(async (alertId: string, context: 'login' | 'refresh' | 'route_change') => {
    try {
      await fetch(`/api/orchestrator/alerts/${alertId}/shown`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ context }),
      })
    } catch (err) {
      console.warn('Failed to mark alert as shown:', err)
    }
  }, [])

  const dismiss = useCallback(async (alertId: string) => {
    try {
      await fetch(`/api/orchestrator/alerts/${alertId}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      })
      // Remove from local state
      setAlerts(prev => prev.filter(a => a.id !== alertId))
      if (interruptiveCandidate?.id === alertId) {
        setInterruptiveCandidate(null)
      }
    } catch (err) {
      console.warn('Failed to dismiss alert:', err)
    }
  }, [interruptiveCandidate])

  const snooze = useCallback(async (alertId: string, minutes: number) => {
    try {
      await fetch(`/api/orchestrator/alerts/${alertId}/snooze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ minutes }),
      })
      // Remove from local state
      setAlerts(prev => prev.filter(a => a.id !== alertId))
      if (interruptiveCandidate?.id === alertId) {
        setInterruptiveCandidate(null)
      }
    } catch (err) {
      console.warn('Failed to snooze alert:', err)
    }
  }, [interruptiveCandidate])

  const acknowledge = useCallback(async (alertId: string) => {
    try {
      await fetch(`/api/orchestrator/alerts/${alertId}/ack`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      })
      // Remove from local state
      setAlerts(prev => prev.filter(a => a.id !== alertId))
      if (interruptiveCandidate?.id === alertId) {
        setInterruptiveCandidate(null)
      }
    } catch (err) {
      console.warn('Failed to acknowledge alert:', err)
    }
  }, [interruptiveCandidate])

  // Initial fetch
  useEffect(() => {
    if (!enabled) return
    fetchAlerts()
  }, [enabled, fetchAlerts])

  // Poll for updates
  useEffect(() => {
    if (!enabled) return
    const interval = setInterval(fetchAlerts, pollIntervalMs)
    return () => clearInterval(interval)
  }, [enabled, fetchAlerts, pollIntervalMs])

  // Refetch on route change (to catch new alerts on navigation)
  useEffect(() => {
    if (!enabled) return
    fetchAlerts()
  }, [enabled, location.pathname, fetchAlerts])

  return useMemo(() => ({
    alerts,
    interruptiveCandidate,
    loading,
    fetchAlerts,
    markShown,
    dismiss,
    snooze,
    acknowledge,
  }), [alerts, interruptiveCandidate, loading, fetchAlerts, markShown, dismiss, snooze, acknowledge])
}

/**
 * Public hook — unchanged call shape. Reads the app-root feed when one is
 * mounted; falls back to its own poller otherwise (tests, isolated mounts).
 */
export function useCoachAlerts(pollIntervalMs: number = 60000): CoachAlertsApi {
  const shared = useContext(CoachAlertsContext)
  const local = useCoachAlertsSource(pollIntervalMs, shared === null)
  return shared ?? local
}
